import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Heart, Lock, Loader2, AlertCircle, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEOHead';
import axios from 'axios';
import API_URL from '../config/api';

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // login | forgot | sent
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      const role = result?.user?.role;
      if (!['admin', 'super_admin', 'support_admin', 'moderator'].includes(role)) {
        setError('Access denied. Admin credentials required.');
        setLoading(false);
        return;
      }
      navigate('/admin');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please enter your admin email'); return; }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setMode('sent');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#2D2D2A] flex items-center justify-center px-4 sm:px-6" data-testid="admin-login-page">
      <SEOHead title="Admin Login" noindex path="/admin/login" />
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Heart className="w-8 h-8 text-[#C97A63]" fill="currentColor" fillOpacity={0.3} />
            <span className="text-2xl font-serif text-white">LusoLove</span>
          </div>
          <p className="text-xs uppercase tracking-[0.25em] text-white/30">Back Office</p>
        </div>

        <div className="bg-[#363633] rounded-2xl p-8 border border-white/5">
          {mode === 'login' && (
            <form onSubmit={handleLogin}>
              <div className="flex items-center gap-2 mb-6">
                <Lock className="w-4 h-4 text-white/40" />
                <h1 className="text-sm font-semibold text-white/60 uppercase tracking-wider">Admin Access</h1>
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3 mb-4" data-testid="admin-login-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-white/40 mb-1.5 uppercase tracking-wider">Email</label>
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@lusolove.com"
                    className="w-full px-4 py-3 bg-[#2D2D2A] border border-white/10 rounded-xl text-white placeholder-white/20 text-sm focus:outline-none focus:border-[#C97A63]/50 transition-colors"
                    data-testid="admin-login-email" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/40 mb-1.5 uppercase tracking-wider">Password</label>
                  <input type="password" required value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password"
                    className="w-full px-4 py-3 bg-[#2D2D2A] border border-white/10 rounded-xl text-white placeholder-white/20 text-sm focus:outline-none focus:border-[#C97A63]/50 transition-colors"
                    data-testid="admin-login-password" />
                </div>
              </div>

              <Button type="submit" disabled={loading}
                className="w-full mt-6 h-12 bg-[#C97A63] hover:bg-[#B86A53] text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                data-testid="admin-login-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign In to Admin'}
              </Button>

              <button type="button" onClick={() => { setMode('forgot'); setError(''); }}
                className="w-full mt-4 text-xs text-white/30 hover:text-[#C97A63] transition-colors"
                data-testid="admin-forgot-password-link">
                Forgot your password?
              </button>
            </form>
          )}

          {mode === 'forgot' && (
            <form onSubmit={handleForgot}>
              <button type="button" onClick={() => { setMode('login'); setError(''); }}
                className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 mb-5 transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to login
              </button>

              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-white/40" />
                <h1 className="text-sm font-semibold text-white/60 uppercase tracking-wider">Reset Password</h1>
              </div>
              <p className="text-xs text-white/30 mb-6">Enter your admin email. We'll send a secure reset link.</p>

              {error && (
                <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3 mb-4">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@lusolove.com"
                className="w-full px-4 py-3 bg-[#2D2D2A] border border-white/10 rounded-xl text-white placeholder-white/20 text-sm focus:outline-none focus:border-[#C97A63]/50 transition-colors"
                data-testid="admin-forgot-email" />

              <Button type="submit" disabled={loading}
                className="w-full mt-4 h-12 bg-[#C97A63] hover:bg-[#B86A53] text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                data-testid="admin-forgot-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Reset Link'}
              </Button>
            </form>
          )}

          {mode === 'sent' && (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-[#C97A63]/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 text-[#C97A63]" />
              </div>
              <h2 className="text-white text-base font-medium mb-2">Reset link sent</h2>
              <p className="text-xs text-white/40 mb-6">Check your inbox for a password reset link. The link expires in 1 hour.</p>
              <button onClick={() => { setMode('login'); setError(''); }}
                className="text-xs text-[#C97A63] hover:text-[#B86A53] transition-colors"
                data-testid="admin-back-to-login">
                Back to login
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-white/15 text-xs mt-6">Authorized personnel only</p>
      </div>
    </div>
  );
}
